import React, { useState, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Button,
  Tooltip,
} from '@mui/material';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import AssignmentIcon from '@mui/icons-material/AssignmentOutlined';
import {
  DragIndicator as DragIcon,
  Code as CodeIcon,
  ErrorOutline as ErrorIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
} from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import CodeMirror from '@uiw/react-codemirror';
import { yaml as yamlLang } from '@codemirror/lang-yaml';
import { EditorView } from '@codemirror/view';
import { HighlightStyle, syntaxHighlighting } from '@codemirror/language';
import { tags } from '@lezer/highlight';
import { vscodeDark } from '@uiw/codemirror-theme-vscode';
import jsYaml from 'js-yaml';
import { CardPlayground } from './CardPlayground';

// ── Styles ───────────────────────────────────────────────────────────────────

const useStyles = makeStyles(() => ({
  splitRoot: {
    display: 'flex',
    flex: 1,
    minHeight: 0,
    backgroundColor: '#0d0d0d',
    overflow: 'hidden',
  },
  leftPane: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#111111',
    overflow: 'hidden',
    flexShrink: 0,
  },
  leftHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '10px 16px',
    borderBottom: '1px solid #2a2a2a',
    backgroundColor: '#161616',
  },
  leftHeaderTitle: {
    color: '#ffffff',
    fontWeight: 600,
    fontSize: '0.8rem',
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
  },
  editorWrapper: {
    flex: 1,
    overflow: 'hidden',
    '& .cm-editor': { height: '100%', fontSize: '0.78rem', backgroundColor: '#1e1e1e' },
    '& .cm-editor.cm-focused': { outline: 'none' },
    '& .cm-scroller': { fontFamily: '"JetBrains Mono","Fira Code",monospace', lineHeight: 1.7, backgroundColor: '#1e1e1e' },
    '& .cm-gutters': { backgroundColor: '#1e1e1e', borderRight: '1px solid #3a3f4b', color: '#636d83' },
    '& .cm-activeLineGutter': { backgroundColor: '#252526' },
    '& .cm-activeLine': { backgroundColor: '#252526' },
    '& .cm-content': { backgroundColor: '#1e1e1e' },
  },
  dragHandle: {
    width: 10,
    flexShrink: 0,
    cursor: 'col-resize',
    backgroundColor: 'transparent',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '&:hover': { backgroundColor: 'rgba(255,255,255,0.08)' },
  },
  dragIcon: { fontSize: 13, color: '#636d83' },
  rightPane: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    backgroundColor: '#f7f8fa',
  },
  rightHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 20px',
    borderBottom: '1px solid #e8eaed',
    backgroundColor: '#ffffff',
    flexShrink: 0,
  },
  stepperWrapper: {
    backgroundColor: '#ffffff',
    borderBottom: '1px solid #e8eaed',
    padding: '4px 20px',
    flexShrink: 0,
    '& .MuiStepLabel-label': { fontSize: '0.72rem', color: '#9ca3af' },
    '& .MuiStepLabel-active': { color: '#111827 !important', fontWeight: 600 },
    '& .MuiStepIcon-root': { color: '#e5e7eb' },
    '& .MuiStepIcon-active': { color: '#111827 !important' },
  },
  formScroll: {
    flex: 1,
    overflowY: 'auto',
    padding: '20px',
    display: 'flex',
    flexDirection: 'column',
  },
  formPaper: {
    backgroundColor: '#ffffff',
    borderRadius: 12,
    border: '1px solid #e8eaed',
    padding: '24px 28px',
    flex: 1,
    '& [type="submit"]': { display: 'none' },
  },
  navRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '10px 20px',
    borderTop: '1px solid #e8eaed',
    backgroundColor: '#ffffff',
    flexShrink: 0,
  },
  emptyState: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    padding: '0 40px',
  },
  errorState: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    padding: '0 40px',
  },
  errorMessage: {
    fontFamily: '"JetBrains Mono","Fira Code",monospace',
    fontSize: '0.72rem',
    color: '#b91c1c',
    backgroundColor: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: 8,
    padding: '10px 14px',
    width: '100%',
    maxWidth: 460,
    lineHeight: 1.7,
    whiteSpace: 'pre-wrap' as const,
    overflowX: 'auto' as const,
  },
  errorDot: { width: 7, height: 7, borderRadius: '50%', backgroundColor: '#ff4444', flexShrink: 0 },
  warningDot: { width: 7, height: 7, borderRadius: '50%', backgroundColor: '#f59e0b', flexShrink: 0 },
  btnBack: {
    border: '1px solid #e5e7eb',
    color: '#374151',
    backgroundColor: '#fff',
    padding: '5px 14px',
    borderRadius: 8,
    fontSize: '0.78rem',
    fontWeight: 500,
    textTransform: 'none' as const,
    '&:hover': { backgroundColor: '#f9fafb' },
  },
  btnNext: {
    border: 'none',
    color: '#fff',
    backgroundColor: '#111827',
    padding: '5px 14px',
    borderRadius: 8,
    fontSize: '0.78rem',
    fontWeight: 500,
    textTransform: 'none' as const,
    '&:hover': { backgroundColor: '#1f2937' },
  },
}));

// ── YAML parser ───────────────────────────────────────────────────────────────

function parseTemplateYaml(raw: string) {
  const empty = { title: '', description: '', tags: [] as string[], steps: [] as any[], error: null as string | null, warnings: [] as string[] };
  try {
    const doc = jsYaml.load(raw) as any;
    if (!doc || doc.kind !== 'Template')
      return { ...empty, error: 'YAML must be a Backstage Template (kind: Template)' };
    const parameters = doc?.spec?.parameters;
    if (!parameters) return { ...empty, error: 'No spec.parameters found in template' };
    const paramArray = Array.isArray(parameters) ? parameters : [parameters];
    return {
      title: doc.metadata?.title || doc.metadata?.name || 'Template',
      description: doc.metadata?.description || '',
      tags: doc.metadata?.tags || [],
      steps: paramArray.map((p: any) => ({
        title: p.title || 'Untitled Step',
        required: Array.isArray(p.required) ? p.required : [],
        properties: p.properties || {},
      })),
      error: null,
      warnings: [] as string[],
    };
  } catch (e: any) {
    return { ...empty, error: e.message };
  }
}

const yamlContentFix = syntaxHighlighting(HighlightStyle.define([{ tag: tags.content, color: '#ce9178' }]));
const cmExtensions = [yamlLang(), yamlContentFix, EditorView.lineWrapping];

const PLACEHOLDER = `apiVersion: scaffolder.backstage.io/v1beta3
kind: Template
metadata:
  name: my-template
  title: My Template
  description: A sample template
  tags:
    - aws
spec:
  parameters:
    - title: Configuration
      required:
        - name
      properties:
        name:
          title: Name
          type: string
`;

// ── Simple field renderer ─────────────────────────────────────────────────────

const SimpleField = ({ fieldKey, field, value, onChange }: { fieldKey: string; field: any; value: any; onChange: (k: string, v: any) => void }) => {
  const label = field.title || fieldKey;
  if (field.enum) {
    return (
      <Box>
        <Typography style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151', marginBottom: 4 }}>{label}</Typography>
        <select
          value={value ?? ''}
          onChange={e => onChange(fieldKey, e.target.value)}
          style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: '0.85rem' }}
        >
          <option value="">Select…</option>
          {field.enum.map((opt: string) => <option key={opt} value={opt}>{opt}</option>)}
        </select>
      </Box>
    );
  }
  if (field.type === 'boolean') {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <input type="checkbox" checked={!!value} onChange={e => onChange(fieldKey, e.target.checked)} />
        <Typography style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151' }}>{label}</Typography>
      </Box>
    );
  }
  return (
    <Box>
      <Typography style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151', marginBottom: 4 }}>{label}</Typography>
      <input
        type={field.type === 'number' || field.type === 'integer' ? 'number' : 'text'}
        value={value ?? ''}
        placeholder={field.description || ''}
        onChange={e => onChange(fieldKey, e.target.value)}
        style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: '0.85rem', boxSizing: 'border-box' }}
      />
    </Box>
  );
};

// ── Template Playground ───────────────────────────────────────────────────────

const TemplatePlaygroundEmbed = () => {
  const classes = useStyles();
  const [leftWidth, setLeftWidth] = useState(42);
  const rootRef = useRef<HTMLDivElement>(null);
  const [isResizing, setIsResizing] = useState(false);
  const [yaml, setYaml] = useState(PLACEHOLDER);
  const [parsed, setParsed] = useState<ReturnType<typeof parseTemplateYaml> | null>(() => parseTemplateYaml(PLACEHOLDER));
  const [activeStep, setActiveStep] = useState(0);
  const [formDataByStep, setFormDataByStep] = useState<Record<number, any>>({});

  const handleMouseDown = useCallback((e: React.MouseEvent) => { e.preventDefault(); setIsResizing(true); }, []);
  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isResizing || !rootRef.current) return;
    const rect = rootRef.current.getBoundingClientRect();
    const pct = ((e.clientX - rect.left) / rect.width) * 100;
    setLeftWidth(Math.min(55, Math.max(28, pct)));
  }, [isResizing]);
  const handleMouseUp = useCallback(() => setIsResizing(false), []);

  React.useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => { document.removeEventListener('mousemove', handleMouseMove); document.removeEventListener('mouseup', handleMouseUp); };
    }
    return undefined;
  }, [isResizing, handleMouseMove, handleMouseUp]);

  const handleYamlChange = useCallback((value: string) => {
    setYaml(value);
    if (!value.trim()) { setParsed(null); setActiveStep(0); setFormDataByStep({}); return; }
    setParsed(parseTemplateYaml(value));
    setActiveStep(0);
    setFormDataByStep({});
  }, []);

  const steps = parsed?.steps ?? [];
  const currentStep = steps[activeStep];
  const hasError = !!parsed?.error;
  const hasSteps = steps.length > 0;

  return (
    <div ref={rootRef} className={classes.splitRoot}>
      {/* Left: YAML editor */}
      <Box className={classes.leftPane} style={{ width: `${leftWidth}%` }}>
        <Box className={classes.leftHeader}>
          <Box style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <CodeIcon style={{ fontSize: 13, color: '#636d83' }} />
            <Typography className={classes.leftHeaderTitle}>Template YAML</Typography>
            {hasError && <Box className={classes.errorDot} />}
          </Box>
        </Box>
        <Box className={classes.editorWrapper}>
          <CodeMirror
            value={yaml}
            placeholder={PLACEHOLDER}
            theme={vscodeDark}
            extensions={cmExtensions}
            onChange={handleYamlChange}
            height="100%"
            basicSetup={{ lineNumbers: true, foldGutter: true, highlightActiveLine: true, autocompletion: false }}
            style={{ height: '100%' }}
          />
        </Box>
      </Box>

      {/* Drag handle */}
      <Box className={classes.dragHandle} onMouseDown={handleMouseDown}>
        <DragIcon className={classes.dragIcon} />
      </Box>

      {/* Right: form preview */}
      <Box className={classes.rightPane}>
        <Box className={classes.rightHeader}>
          <Typography style={{ fontWeight: 600, fontSize: '0.78rem', letterSpacing: '0.06em', textTransform: 'uppercase', color: '#9ca3af' }}>
            Form Preview
          </Typography>
          {hasSteps && parsed?.title && (
            <Typography style={{ fontWeight: 700, fontSize: '0.95rem', color: '#111827' }}>{parsed.title}</Typography>
          )}
        </Box>

        {hasError ? (
          <Box className={classes.errorState}>
            <Box style={{ width: 48, height: 48, borderRadius: '50%', backgroundColor: '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <ErrorIcon style={{ fontSize: 24, color: '#ef4444' }} />
            </Box>
            <Typography style={{ fontWeight: 600, color: '#111827', fontSize: '0.9rem' }}>Invalid Template YAML</Typography>
            <Box className={classes.errorMessage}>{parsed!.error}</Box>
          </Box>
        ) : !hasSteps ? (
          <Box className={classes.emptyState}>
            <Box style={{ width: 48, height: 48, borderRadius: '50%', backgroundColor: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <AssignmentIcon style={{ fontSize: 24, color: '#9ca3af' }} />
            </Box>
            <Typography style={{ fontWeight: 600, color: '#374151', fontSize: '0.9rem' }}>No template loaded</Typography>
            <Typography style={{ color: '#9ca3af', textAlign: 'center', maxWidth: 240, lineHeight: 1.6, fontSize: '0.82rem' }}>
              Paste a Backstage Template YAML on the left to preview the form
            </Typography>
          </Box>
        ) : (
          <>
            {steps.length > 1 && (
              <Box className={classes.stepperWrapper}>
                <Stepper activeStep={activeStep} alternativeLabel>
                  {steps.map((s, i) => <Step key={i}><StepLabel>{s.title}</StepLabel></Step>)}
                </Stepper>
              </Box>
            )}
            <Box className={classes.formScroll}>
              <Paper className={classes.formPaper} elevation={0}>
                <Typography style={{ fontWeight: 700, fontSize: '1rem', color: '#111827', marginBottom: 6 }}>{currentStep?.title}</Typography>
                {parsed?.description && (
                  <Typography style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: 20, lineHeight: 1.5 }}>{parsed.description}</Typography>
                )}
                <Box style={{ height: 1, backgroundColor: '#f3f4f6', margin: '16px 0' }} />
                {currentStep && (
                  <Box style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                    {Object.entries(currentStep.properties).map(([key, field]: [string, any]) => (
                      <SimpleField
                        key={key}
                        fieldKey={key}
                        field={field}
                        value={formDataByStep[activeStep]?.[key]}
                        onChange={(k, v) => setFormDataByStep(prev => ({ ...prev, [activeStep]: { ...prev[activeStep], [k]: v } }))}
                      />
                    ))}
                  </Box>
                )}
              </Paper>
            </Box>
            <Box className={classes.navRow}>
              <Typography style={{ fontSize: '0.72rem', color: '#9ca3af', fontWeight: 500 }}>
                Step {activeStep + 1} of {steps.length}
              </Typography>
              <Box style={{ display: 'flex', gap: 8 }}>
                {activeStep > 0 && (
                  <Button className={classes.btnBack} startIcon={<ArrowBackIcon style={{ fontSize: 13 }} />} onClick={() => setActiveStep(s => s - 1)}>Back</Button>
                )}
                {activeStep < steps.length - 1 && (
                  <Button className={classes.btnNext} endIcon={<ArrowForwardIcon style={{ fontSize: 13 }} />} onClick={() => setActiveStep(s => s + 1)}>Next</Button>
                )}
              </Box>
            </Box>
          </>
        )}
      </Box>
    </div>
  );
};

// ── PlaygroundTab ─────────────────────────────────────────────────────────────

const SUB_TABS = [
  {
    label: 'Asset',
    desc: 'Build a home.yaml card entry and see a live preview of how it will appear on the Marketplace home page.',
  },
  {
    label: 'Template',
    desc: 'Paste a Backstage scaffolder Template YAML and preview the generated form in real-time.',
  },
];

interface PlaygroundTabProps {
  onGoToTrainingDocs: (pageId?: string) => void;
}

export const PlaygroundTab: React.FC<PlaygroundTabProps> = ({ onGoToTrainingDocs }) => {
  const [subTab, setSubTab] = useState(0);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/* Sub-tab bar */}
      <Box sx={{
        px: 3,
        py: 1.5,
        bgcolor: 'white',
        borderBottom: '1px solid rgba(0,0,0,0.08)',
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 3,
      }}>
        {/* Segmented control */}
        <Box sx={{
          display: 'flex',
          bgcolor: '#f3f4f6',
          borderRadius: '10px',
          p: '4px',
          gap: '2px',
          flexShrink: 0,
          border: '1px solid rgba(0,0,0,0.06)',
        }}>
          {SUB_TABS.map((t, i) => (
            <Tooltip key={i} title={t.desc} placement="bottom" arrow enterDelay={600}>
              <Box
                onClick={() => setSubTab(i)}
                sx={{
                  px: 2,
                  py: 0.75,
                  borderRadius: '7px',
                  fontSize: '0.82rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.18s ease',
                  whiteSpace: 'nowrap',
                  userSelect: 'none',
                  bgcolor: subTab === i ? 'white' : 'transparent',
                  color: subTab === i ? '#1a202c' : '#9ca3af',
                  boxShadow: subTab === i ? '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.06)' : 'none',
                  '&:hover': { color: subTab === i ? '#1a202c' : '#4b5563', bgcolor: subTab === i ? 'white' : 'rgba(0,0,0,0.03)' },
                }}
              >
                {t.label}
              </Box>
            </Tooltip>
          ))}
        </Box>

        {/* Guide link — always visible for both sub-tabs */}
        <Box
          onClick={() => onGoToTrainingDocs('883967732')}
          sx={{
            display: 'flex', alignItems: 'center', gap: 0.75,
            fontSize: '0.78rem', fontWeight: 600, color: '#2563eb',
            cursor: 'pointer', flexShrink: 0,
            px: 1.5, py: 0.75, borderRadius: '8px',
            border: '1px solid #dbeafe', bgcolor: '#eff6ff',
            '&:hover': { bgcolor: '#dbeafe' },
          }}
        >
          <MenuBookIcon sx={{ fontSize: 15 }} />
          Playground Guide
        </Box>
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        {subTab === 0 && <CardPlayground />}
        {subTab === 1 && <TemplatePlaygroundEmbed />}
      </Box>
    </Box>
  );
};
